import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input, FormField } from './Form';
import { useToast } from './Toast';
import { redirectsApi } from '../../services/toolsApi';
import './AliasManageModal.scss';

// Reserved URLs that cannot be used as aliases (defined outside component to avoid re-creation)
const RESERVED_URLS = [
  'stats', 'api', 'hs', 'index', 'admin', 'dashboard', 'app', 'www',
  'mail', 'email', 'ftp', 'blog', 'dev', 'test', 'staging', 'prod',
  'production', 'cdn', 'assets', 'static', 'public', 'private', 'secure',
  'login', 'logout', 'auth', 'oauth', 'register', 'signup', 'signin',
  'account', 'profile', 'settings', 'config', 'help', 'support', 'contact',
  'about', 'terms', 'privacy', 'legal', 'robots', 'sitemap', 'favicon',
  'apple-touch-icon',
];

/**
 * AliasManageModal - Modal for creating, updating, and deleting redirect aliases
 * 
 * Allows admins to create friendly URLs like go.clozesure.com/my-campaign
 * instead of go.clozesure.com/hs/160884927-POS/booklet
 * 
 * Validation rules:
 * - 3-50 characters
 * - Lowercase alphanumeric + hyphens only
 * - Cannot start/end with hyphen or have consecutive hyphens
 * - Reserved words blocked (stats, api, hs, admin, etc.)
 * - Duplicate aliases blocked
 */
const AliasManageModal = ({
  isOpen,
  onClose,
  campaignId,
  campaignType,
  displayName,
  currentAlias = null,
  currentPath = '',
  onSuccess,
}) => {
  const toast = useToast();
  const [aliasInput, setAliasInput] = useState('');
  const [landingPath, setLandingPath] = useState('');
  const [validationState, setValidationState] = useState({
    isValid: false,
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  // Ensure campaignType is never null (null doesn't trigger default parameters)
  const normalizedCampaignType = campaignType || 'default';

  // Initialize with current alias + landing path if editing
  useEffect(() => {
    if (isOpen) {
      setAliasInput(currentAlias || '');
      setLandingPath(currentPath || '');
      setValidationState({ isValid: false, message: '' });
    }
  }, [isOpen, currentAlias, currentPath]);

  // Validate alias input
  useEffect(() => {
    if (!aliasInput) {
      setValidationState({ isValid: false, message: '' });
      return;
    }

    const value = aliasInput.toLowerCase().trim();

    // Validation rules (alias name)
    const isValidLength =value.length >= 3 && value.length <= 50;
    const isValidChars = /^[a-z0-9-]+$/.test(value);
    const isValidFormat =
      !value.startsWith('-') &&
      !value.endsWith('-') &&
      !value.includes('--');
    const isNotReserved = !RESERVED_URLS.includes(value);

    // Landing path: blank = homepage; otherwise one or more lowercase
    // alphanumeric/hyphen segments joined by single slashes (no leading/
    // trailing/double slash).
    const cleanPath = landingPath.trim().replace(/^\/+|\/+$/g, '');
    const isValidPath =
      cleanPath === '' || /^[a-z0-9-]+(\/[a-z0-9-]+)*$/.test(cleanPath);

    // Unchanged only if BOTH the name and the landing path are unchanged
    const prevPath = (currentPath || '').trim().replace(/^\/+|\/+$/g, '');
    const nameUnchanged = currentAlias && value === currentAlias.toLowerCase();
    const isUnchanged = nameUnchanged && cleanPath === prevPath;

    if (!isValidLength) {
      setValidationState({
        isValid: false,
        message: 'Must be 3-50 characters long',
      });
    } else if (!isValidChars) {
      setValidationState({
        isValid: false,
        message: 'Only lowercase letters, numbers, and hyphens allowed',
      });
    } else if (!isValidFormat) {
      setValidationState({
        isValid: false,
        message: 'Cannot start/end with hyphen or have consecutive hyphens',
      });
    } else if (!isNotReserved) {
      setValidationState({
        isValid: false,
        message: 'This name is reserved and cannot be used',
      });
    } else if (!isValidPath) {
      setValidationState({
        isValid: false,
        message: 'Landing path: lowercase letters, numbers, hyphens and / only',
      });
    } else if (isUnchanged) {
      // Nothing changed yet — keep Save disabled, but show no message. A red
      // "No changes made" on open reads as an error for something the user
      // hasn't done.
      setValidationState({
        isValid: false,
        message: '',
      });
    } else {
      setValidationState({
        isValid: true,
        message: '',
      });
    }
  }, [aliasInput, landingPath, currentAlias, currentPath]);

  // Handle save alias
  const handleSave = async () => {
    if (!validationState.isValid) return;

    setIsLoading(true);
    try {
      const alias = aliasInput.toLowerCase().trim();
      // The landing path rides inside campaignType as "source/path" so the
      // existing `go` resolver ( /hs/{campaignId}/{campaignType} ) sends the
      // visitor to www.clozesure.com/{path}. Blank path = source only = homepage.
      const cleanPath = landingPath.trim().replace(/^\/+|\/+$/g, '');
      const storedType = cleanPath
        ? `${normalizedCampaignType}/${cleanPath}`
        : normalizedCampaignType;
      const result = await redirectsApi.createAlias(alias, campaignId, storedType);

      if (result.success) {
        toast.success(result.message || 'Alias saved successfully', {});
        if (onSuccess) {
          onSuccess(alias);
        }
        onClose();
      } else {
        toast.error(result.message || 'Failed to save alias', {
          debugInfo: {
            operation: 'createAlias',
            params: { alias, campaignId, campaignType: normalizedCampaignType },
            error: result.error || 'Unknown error',
          },
        });
      }
    } catch (error) {
      console.error('Error saving alias:', error);
      
      // Special handling for "already exists" on same campaign
      // This can happen when the alias data wasn't loaded properly
      if (error.message && error.message.includes('already in use by another campaign')) {
        toast.error('This alias is already in use by another campaign', {
          debugInfo: {
            operation: 'createAlias',
            params: { alias: aliasInput, campaignId, campaignType: normalizedCampaignType },
            error: error.stack || error.toString(),
            note: 'Alias belongs to a different campaign - choose a different name',
          },
        });
      } else {
        toast.error(error.message || 'Failed to save alias', {
          debugInfo: {
            operation: 'createAlias',
            params: { alias: aliasInput, campaignId, campaignType: normalizedCampaignType },
            error: error.stack || error.toString(),
          },
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete alias
  const handleDelete = async () => {
    if (!currentAlias) return;

    const confirmed = window.confirm(
      'Are you sure you want to delete this alias? The redirect will stop working.'
    );
    if (!confirmed) return;

    setIsLoading(true);
    try {
      const result = await redirectsApi.deleteAlias(currentAlias);

      if (result.success) {
        toast.success(result.message || 'Alias deleted successfully', {});
        if (onSuccess) {
          onSuccess(null);
        }
        onClose();
      } else {
        toast.error(result.message || 'Failed to delete alias');
      }
    } catch (error) {
      console.error('Error deleting alias:', error);
      toast.error(error.message || 'Failed to delete alias', {
        debugInfo: {
          operation: 'deleteAlias',
          params: { alias: currentAlias },
          error: error.stack || error.toString(),
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change (auto-lowercase)
  const handleInputChange = (e) => {
    const value = e.target.value.toLowerCase();
    setAliasInput(value);
  };

  const modalTitle = campaignType !== 'default'
    ? `Manage Alias for ${displayName}`
    : `Manage Campaign Alias for ${displayName}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      className="alias-manage-modal"
    >
      <div className="alias-modal-content">
        {/* Current alias display */}
        {currentAlias && (
          <div className="current-alias-section">
            <label>Current Alias:</label>
            <div className="current-alias-url">
              go.clozesure.com/{currentAlias}
            </div>
          </div>
        )}

        {/* Alias input */}
        <div className="alias-input-section">
          <FormField
            label="Alias URL Path"
            helpText="3-50 characters, lowercase letters, numbers, and hyphens only"
          >
            <div className="alias-input-wrapper">
              <Input
                value={aliasInput}
                onChange={handleInputChange}
                placeholder="my-campaign-name"
                className={
                  aliasInput
                    ? validationState.isValid
                      ? 'valid'
                      : 'invalid'
                    : ''
                }
                disabled={isLoading}
                autoFocus
              />
              {aliasInput && (
                <span className={`validation-icon ${validationState.isValid ? 'valid' : 'invalid'}`}>
                  {validationState.isValid ? '✓' : '✗'}
                </span>
              )}
            </div>
          </FormField>

          {/* Fixed-height feedback area */}
          <div className="alias-feedback">
            {aliasInput && !validationState.isValid && validationState.message && (
              <div className="feedback-message error">
                {validationState.message}
              </div>
            )}
            {validationState.isValid && aliasInput && (
              <div className="feedback-message success">
                <strong>Preview:</strong> go.clozesure.com/{aliasInput.toLowerCase().trim()}
              </div>
            )}
          </div>
        </div>

        {/* Landing path (optional) — which page on the site to send visitors to */}
        <div className="alias-input-section">
          <FormField
            label="Landing path (optional)"
            helpText="Leave blank to send to the homepage, or enter a page path (e.g. conveyancers)."
          >
            <Input
              value={landingPath}
              onChange={(e) => setLandingPath(e.target.value.toLowerCase())}
              placeholder="(homepage)"
              disabled={isLoading}
              fullWidth
            />
          </FormField>
          <div
            style={{
              fontSize: '0.8rem',
              color: '#475569',
              marginTop: 6,
              wordBreak: 'break-all',
            }}
          >
            Sends to{' '}
            <strong style={{ color: '#1e3a8a' }}>
              www.clozesure.com/{landingPath.trim().replace(/^\/+|\/+$/g, '')}
            </strong>
          </div>
        </div>

        {/* Actions */}
        <div className="alias-modal-actions">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>

          {currentAlias && (
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={isLoading}
            >
              {isLoading ? 'Deleting...' : 'Delete Alias'}
            </Button>
          )}

          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!validationState.isValid || isLoading}
          >
            {isLoading
              ? 'Saving...'
              : currentAlias
              ? 'Update Alias'
              : 'Save Alias'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

AliasManageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  campaignId: PropTypes.string, // Allow null when modal is closed
  campaignType: PropTypes.string,
  displayName: PropTypes.string, // Allow null when modal is closed
  currentAlias: PropTypes.string,
  currentPath: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default AliasManageModal;
